import type { ReactNode } from 'react'
import { ChevronDown, SlidersHorizontal } from 'lucide-react'
import { ActionPopover } from '@/components/action-popover'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * A list page's search box and filters (USA-1537, USA-1582). The search box stays in view with a
 * Filters button beside it; the filters open from the button as one labeled panel, a popover on
 * wider screens and a bottom sheet on phones, so a page never grows a row of dropdowns. `active`
 * counts the filters set away from their defaults; the button shows it, and `onClear` resets them.
 * `count` is how many results match the search and filters, shown at the end of the row (USA-1634).
 */
export function ListFilters({
  search,
  active,
  onClear,
  actions,
  count,
  children,
}: {
  /** Left out on a page with nothing to search: the Filters button stands alone. */
  search?: ReactNode
  active: number
  onClear?: () => void
  /** How many results match, from `resultCount` in `@/lib/utils`; nothing shows while it is unknown. */
  count?: string
  /** Controls that stay beside the Filters button, e.g. a view toggle. */
  actions?: ReactNode
  /** The filters, each in a `ListFilter`. */
  children: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {search != null && <div className="min-w-0 flex-1 basis-56 sm:max-w-md">{search}</div>}
      <ActionPopover
        title="Filters"
        align="start"
        contentClassName="w-[min(22rem,calc(100vw-1.5rem))]"
        trigger={
          <Button type="button" variant="outline" className="h-9 shrink-0 gap-1.5 px-3">
            <SlidersHorizontal aria-hidden className="h-4 w-4" />
            Filters
            {active > 0 && (
              <span className="rounded-full bg-primary px-1.5 text-[11px] font-medium tabular-nums text-primary-foreground">
                {active}
                <span className="sr-only"> set</span>
              </span>
            )}
            <ChevronDown aria-hidden className="h-4 w-4" />
          </Button>
        }
      >
        <div className="flex flex-col gap-3">{children}</div>
        {onClear && active > 0 && (
          <Button type="button" variant="ghost" size="sm" className="self-start px-0 text-muted-foreground" onClick={onClear}>
            Clear filters
          </Button>
        )}
      </ActionPopover>
      {actions}
      <ResultCount>{count}</ResultCount>
    </div>
  )
}

/** The match count at the end of a list's search row, for pages whose search sits outside `ListFilters`. */
export function ResultCount({ children, className }: { children?: string; className?: string }) {
  if (!children) return null
  return (
    <p aria-live="polite" className={cn('ml-auto whitespace-nowrap text-sm tabular-nums text-muted-foreground', className)}>
      {children}
    </p>
  )
}

/** One filter in the panel: its name above its control, which fills the width. */
export function ListFilter({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 [&>*]:!w-full">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </div>
  )
}
